import React, { createContext, useEffect, type ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@apollo/client/react'
import { GET_USER_ACCOUNT } from '../graphql/queries/userAccount'
import type { GetUserAccountQuery } from '../types/graphql-generated'
import { posthog } from '../lib/posthog'
import { USER_ACCOUNT_POLL_INTERVAL_MS } from '../appConfig'

// Basic user account info as returned by the query (without full fantasy league member data)
export type BasicUserAccount = NonNullable<GetUserAccountQuery['userAccount']>;

export interface UserAccountContextValue {
  userAccount: BasicUserAccount | null | undefined
  loading: boolean
  error: Error | undefined
  // Returns the in-flight query promise so callers (e.g. pull-to-refresh) can
  // await the actual network round-trip instead of resolving immediately.
  refetch: () => Promise<unknown>
}

// eslint-disable-next-line react-refresh/only-export-components
export const UserAccountContext = createContext<UserAccountContextValue | undefined>(undefined)

interface UserAccountProviderProps {
  children: ReactNode
}

export const UserAccountProvider: React.FC<UserAccountProviderProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth0()

  useEffect(() => {
    if (user?.sub) {
      posthog.identify(user.sub)
    }
  }, [user?.sub])

  const { data, previousData, loading: queryLoading, error, refetch } = useQuery<GetUserAccountQuery>(
    GET_USER_ACCOUNT,
    {
      skip: !user || authLoading,
      pollInterval: USER_ACCOUNT_POLL_INTERVAL_MS,
    }
  )
  const accountData = data ?? previousData

  const value: UserAccountContextValue = {
    userAccount: accountData?.userAccount,
    // Only report `loading` for the very first fetch (no data yet). Apollo also
    // flips `queryLoading` to true on every background refetch (pull-to-refresh,
    // ProfileAvatar update, league search, ...), and several consumers
    // (AppInitializer, ProfileGuard, ProfileTab) render a full-screen spinner
    // while `loading` is true. Reporting refetches as "loading" made those
    // consumers tear down and remount the whole tree, causing a screen flicker.
    loading: authLoading || (queryLoading && !accountData),
    // A failed background poll must not tear down screens that can continue
    // using the last successful account response. Initial-load failures remain
    // visible so AppInitializer can show its server error screen.
    error: accountData ? undefined : error,
    refetch,
  }

  return <UserAccountContext.Provider value={value}>{children}</UserAccountContext.Provider>
}
